import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Fuse from 'fuse.js'

import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";

import { AutoCompleteField, Button, CalendarField, DropDownField, TextInputField } from "../../../helpers";

import Loading from "../../../common/Loading";

import ConfirmModal from "./ConfirmModal";

import { insertVendorRateContract } from "../../../services/api/vendorRateContract";
import { getMaterialMasterAndTempMaterialIds, getMaterialMasterAndTempMaterialIdsByBrandId } from "../../../services/api/materialMaster";

import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { updateVendorRateContractItems } from "../../../services/redux/reducers/vendorRateContractItems/vendorRateContractItems";

import { getListDiscountAndUnitPrices } from "../../../utils";


const AddVendorRCItem = (props) => {
    const {
        materialBrands,
        materialCategories,
        vendorRCTypeFields,
        vendorRCCategoryFields,
        vendorRCTypes,
        vendorRCCoverageTypes,
        vendorRCTypesInfo,
        vendorRCCoverageTypesInfo,
        vendorRateContract,
        setVendorRateContract,
        setVisibleAddVendorRCItemModal,
        setIsLoading,
        materialBrandSearchMethod
    } = props

    const [declaration, setDeclaration] = useState(false)
    const [isModalLoading, setIsModalLoading] = useState(false)
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
    const [isProductDataLoading, setIsProductDataLoading] = useState(false)

    const [vendorRateContractItemObj, setVendorRateContractItemObj] = useState({isNew: true})

    const [materialDetails, setMaterialDetails] = useState([])
    const [materialSuggestions, setMaterialSuggestions] = useState([])
    const [uniqueMaterialSuggestions, setUniqueMaterialSuggestions] = useState([])
    const [variantNameSuggestions, setVariantNameSuggestions] = useState([])

    const dispatch = useDispatch()

    const vendorRateContractItemsData = useSelector(state => state.vendorRateContractItems.vendorRateContractItems)

    const materialFuzzSearch = useCallback((searchTerm) => {
        const fuse = new Fuse(materialDetails, {
            keys: ['productName'],
            includeScore: true,
            threshold: 0.5,
            ignoreLocation: true,
            shouldSort: true,
            getFn: (obj) => obj.productName.replace(/\s+/g, ''),
        })
    
        const result = fuse.search(searchTerm)
    
        const uniqueResults = []
        const productNamesSet = new Set()
    
        result.forEach(item => {
            const productName = item.item.productName
            if (!productNamesSet.has(productName)) {
                productNamesSet.add(productName)
                uniqueResults.push(item)
            }
        })
    
        return {result, uniqueResults}
    }, [materialDetails])

    useEffect(() => {
        if (vendorRateContractItemObj.brand && vendorRCCategoryFields.includes("Product Name")){
            getProductDetails()
        }
    }, [vendorRateContractItemObj.brand])

    const getProductDetails = async () => {
        setIsProductDataLoading(true)

        const materialDetailsRes = await getMaterialMasterAndTempMaterialIdsByBrandId(vendorRateContractItemObj.brand)

        if (materialDetailsRes.failure){
            setIsProductDataLoading(false)
            return dispatch(raiseToast({severity:'error', detail: materialDetailsRes.data.error, summary:'Material Details are not fetched.', life: 3000}))
        }

        setIsProductDataLoading(false)
        setMaterialDetails(materialDetailsRes.data)
    }


    const productNameSearch = (event) => {
        if (event.query === "") {
            setMaterialSuggestions([...materialDetails])
        }
        else {
            const searchResultObj = materialFuzzSearch(event.query)
            
            const totalProductsResults = searchResultObj.result.map(eachProduct => eachProduct.item)
            const relatedUniqueProducts = searchResultObj.uniqueResults.map(eachProduct => eachProduct.item)

            setUniqueMaterialSuggestions(relatedUniqueProducts)
            setMaterialSuggestions(totalProductsResults)
        }
    }


    const handleVendorRateContractItemChange = (e) => {
        if (e.target.name === "productName"){
            const relatedVariantNameSuggestionMaterials = []
            materialSuggestions.map(eachMaterial => {
                if (eachMaterial.brandId === vendorRateContractItemObj.brand && eachMaterial.productName === e.target.value?.productName){
                    if (eachMaterial.variantName){
                        relatedVariantNameSuggestionMaterials.push(eachMaterial)
                    }
                }
            })
            setVariantNameSuggestions(relatedVariantNameSuggestionMaterials)
        }else if (e.target.name === "brand"){
            setVendorRateContractItemObj(prevState => ({...prevState, "category": null, "productName": null, "variantName": null}))
        }else if (["listPrice", "discount", "contractPrice"].includes(e.target.name)){
            let relatedVendorRCType = vendorRCTypes.find(eachVendorRCType => eachVendorRCType.vendorRCTypeId === vendorRateContract.vendorRCTypeId)
            let relatedVendorRCTypeFields = vendorRCTypesInfo[relatedVendorRCType.vendorRCType].columns

            if (relatedVendorRCTypeFields.includes("List Price")){
                let listPrice = vendorRateContractItemObj.listPrice ? vendorRateContractItemObj.listPrice : 0
                let discount = vendorRateContractItemObj.discount ? vendorRateContractItemObj.discount : 0
                let unitPrice = vendorRateContractItemObj.contractPrice ? vendorRateContractItemObj.contractPrice : 0

                let value = e.target.value

                if(value.length > 1 && value[0] === "0"){
                    value = value.substring(1)
                }else if (value === ""){
                    value = 0
                }

                if (e.target.name === "listPrice"){
                    listPrice = value
                }else if (e.target.name === "discount"){
                    discount = value
                }else if (e.target.name === "contractPrice"){
                    unitPrice = value
                }

                let updatedValues = getListDiscountAndUnitPrices(e.target.name, listPrice, discount, unitPrice)
                setVendorRateContractItemObj(prevState => ({...prevState, listPrice: updatedValues.listPrice, discount: updatedValues.discount ? updatedValues.discount : "0", contractPrice: updatedValues.unitPrice}))
            }
        }

        setVendorRateContractItemObj(prevState => ({...prevState, [e.target.name]: e.target.value}))
    }

    const handleOnMaterialIdBlur = async (e) => {
        let materialId = e.target.value.trim()
    
        setIsModalLoading(true)
        const materialDetailsRes = await getMaterialMasterAndTempMaterialIds({materialIds: [materialId]})
        setIsModalLoading(false)
        if (materialDetailsRes.failure){
            return dispatch(raiseToast({severity:'error', detail: materialDetailsRes.data.error, summary:'Material Details are not fetched.', life: 3000}))
        }

        if (materialDetailsRes.data.length){
            setMaterialDetails(materialDetailsRes.data)
        }else {
            setVendorRateContractItemObj(prevState => ({...prevState, "materialId": null}))
            return dispatch(raiseToast({severity:'error', detail: 'Provided Material Id is Invalid'}))
        }
    }

    const renderRCCategoryFields = (vendorRCCategoryFields) => {
        return vendorRCCategoryFields.map(eachField => {
            if (eachField === "Brand"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg-col-5 lg:mb-0 branch-input company-branch">
                        {/* <DropDownField name="brand" labelText="Brand" options={materialBrands} optionLabel="brandName" optionValue="brandId" onChange={handleVendorRateContractItemChange} value={vendorRateContractItemObj.brand}/> */}
                        <AutoCompleteField dropdown name="brand" labelText="Brand" value={vendorRateContractItemObj.brand} field="brandName" dropdownButton searchMethod={materialBrandSearchMethod} suggestions={materialBrands} onChange={handleVendorRateContractItemChange} forceSelection required />
                    </div>
                )
            }else if (eachField === "Category"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-5 lg:mb-0">
                        <DropDownField name="category" labelText="Category" options={materialCategories} optionLabel="categoryName" optionValue="categoryId" onChange={handleVendorRateContractItemChange} value={vendorRateContractItemObj.category} disabled={!vendorRateContractItemObj.brand}/>
                    </div>
                )
            }else if (eachField === "Product Name" && vendorRateContract.vendorRCCoverageTypeId !== 4){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-5 lg:mb-0 post-req-autocomplete">
                        <AutoCompleteField dropdown name="productName" labelText="Product Name" value={vendorRateContractItemObj.productName} field="productName" dropdownButton ClassName="styled-autocomplete-dropdown-button" searchMethod={productNameSearch} suggestions={uniqueMaterialSuggestions} onChange={handleVendorRateContractItemChange} forceSelection required {...(isProductDataLoading ? { loading: true, disabled: true } : {})}/>
                    </div>
                )
            }else if (eachField === "Variant Name"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-5 lg:mb-0 pm-branch flex-1">
                        <DropDownField name="variantName" labelText="Variant Name" options={variantNameSuggestions} optionLabel="variantName" optionValue='variantName' value={vendorRateContractItemObj.variantName} disabled={!vendorRateContractItemObj?.productName} onChange={handleVendorRateContractItemChange}/>
                    </div>
                )
            }else if (eachField === "Material Id"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-5 lg:mb-0 pm-branch md:w-4">
                        <TextInputField name="materialId" labelText="Material Id" value={vendorRateContractItemObj.materialId} keyfilter={"pnum"} maxLength={8} onChange={handleVendorRateContractItemChange} onBlur={handleOnMaterialIdBlur}/>
                    </div>
                )
            }else if (eachField === "Valid From"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <CalendarField name="validFrom" labelText="Valid From" value={vendorRateContractItemObj.validFrom} onChange={(e) => handleVendorRateContractItemChange(e)} />
                    </div>
                )
            }else if (eachField === "Valid To"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <CalendarField name="validTo" labelText="Valid To" value={vendorRateContractItemObj.validTo} onChange={(e) => handleVendorRateContractItemChange(e)} />
                    </div>
                )
            }
        })
    }

    const renderRCTypeFields = (vendorRCTypeFields) => {
        return vendorRCTypeFields.map(eachField => {
            if (eachField === "List Price"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <TextInputField name="listPrice" labelText="List Price" value={vendorRateContractItemObj.listPrice} keyfilter={"pnum"} onChange={(e) => handleVendorRateContractItemChange(e)}/>
                    </div>
                )
            }else if (eachField === "Discount"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <TextInputField name="discount" labelText="Discount" value={vendorRateContractItemObj.discount} keyfilter={"pnum"} onChange={(e) => handleVendorRateContractItemChange(e)}/>
                    </div>     
                )
            }else if (eachField === "Fixed Price"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <TextInputField name="contractPrice" labelText="Fixed Price" value={vendorRateContractItemObj.contractPrice} keyfilter={"pnum"} onChange={(e) => handleVendorRateContractItemChange(e)}/>
                    </div>
                )
            }else if (eachField === "List Price Date"){
                return (
                    <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                        <CalendarField name="listPriceDate" maxDate={new Date()} labelText="List Price Date" value={vendorRateContractItemObj.listPriceDate} onChange={(e) => handleVendorRateContractItemChange(e)} />
                    </div>
                )
            }
        })
    }

    const handleAddVendorRCItem = async () => {
        let newVendorRateContractItem = {
            vendorRateContractId: vendorRateContract.vendorRateContractId,
            vendorRateContractItemId: null,
            brandId: vendorRateContractItemObj.brand?.brandId,
            categoryId: vendorRateContractItemObj.category,
            productName: vendorRateContractItemObj.productName?.productName,
            variantName: vendorRateContractItemObj.variantName,
            materialId: vendorRateContractItemObj.materialId,
            listPrice: vendorRateContractItemObj.listPrice,
            discount: vendorRateContractItemObj.discount,
            contractPrice: vendorRateContractItemObj.contractPrice,
            leadTime: vendorRateContractItemObj.leadTime,
            listPriceDate: vendorRateContractItemObj.listPriceDate,
            validFrom: vendorRateContractItemObj.validFrom,
            validTo: vendorRateContractItemObj.validTo,
            statusId: 1,
            isNew: true,
        }

        let relatedVendorRCType = vendorRCTypes.find(eachVendorRCType => eachVendorRCType.vendorRCTypeId === vendorRateContract.vendorRCTypeId)
        let relatedVendorRCCoverageType = vendorRCCoverageTypes.find(eachVendorRCCoverageType => eachVendorRCCoverageType.vendorRCCoverageTypeId === vendorRateContract.vendorRCCoverageTypeId)

        const validationRequiredKeysForRCTypes = vendorRCTypesInfo[relatedVendorRCType.vendorRCType].validateKeys
        const validationRequiredKeysForRCCoverageTypes = vendorRCCoverageTypesInfo[relatedVendorRCCoverageType.vendorRCCoverageType].validateKeys

        let validationRequiredKeys = [...validationRequiredKeysForRCCoverageTypes, ...validationRequiredKeysForRCTypes]

        let updatedVendorRateContractItems = [newVendorRateContractItem]

        let hasMissingKeys = []

        validationRequiredKeys.map(eachKey => {
            updatedVendorRateContractItems.map(eachVendorRateContractItem => {
                if (!eachVendorRateContractItem[eachKey] && eachVendorRateContractItem.statusId === 1){
                    hasMissingKeys.push(eachKey)   
                }
            })
        })

        if (hasMissingKeys.length){
            return dispatch(raiseToast({severity:'error', detail: `Please enter ${hasMissingKeys[0]}`}))
        }

        let data = {
            vendorRateContract: {...vendorRateContract, isNew: false},
            vendorRateContractItems: updatedVendorRateContractItems
        }

        if (!hasMissingKeys.length){
            if (relatedVendorRCCoverageType.vendorRCCoverageTypeId === 4){

                if (!newVendorRateContractItem.materialId){
                    return dispatch(raiseToast({severity:'error', detail: `Please provide material Id`, life: 3000}))
                }else {
                    setIsConfirmModalVisible(true)
                }
            }else{
                if (window.confirm("Are you sure you want to continue...?")){
                    setIsLoading(true)
                    const vendorRateContractInsertionRes = await insertVendorRateContract(data)

                    if (vendorRateContractInsertionRes.failure){
                        setIsLoading(false)
                        return dispatch(raiseToast({severity:'error', detail: vendorRateContractInsertionRes.data.error, summary: 'Vendor Rate Contract is not Inserted', life: 3000}))
                    }

                    let updatedVendorRateContractItemsData = [...vendorRateContractInsertionRes.data.vendorRateContractItemsRes, ...vendorRateContractItemsData]
                    dispatch(updateVendorRateContractItems(updatedVendorRateContractItemsData))
                    setVisibleAddVendorRCItemModal(false)
                    dispatch(raiseToast({severity:'success', detail: 'Vendor Rate Contract created Successfully', life: 3000}))
                    
                    setIsLoading(false)
                }
            }
        }
    }

    return (
        <>
            <Loading visible={isModalLoading}/>
            <div className='flex align-items-center w-100'>
                <div className='flex flex-row flex-wrap'>
                    <div className="flex flex-row flex-wrap">
                        {
                            renderRCCategoryFields(vendorRCCategoryFields)
                        }
                        {
                            renderRCTypeFields(vendorRCTypeFields)
                        }
                        <div className="flex flex-column col-12 md:col-4 lg:col-4 lg:mb-0 pm-branch md:w-4">
                            <TextInputField name="leadTime" labelText="Lead Time" value={vendorRateContractItemObj.leadTime} keyfilter={"pnum"} onChange={(e) => handleVendorRateContractItemChange(e)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="font-bold mt-4">
                ACKNOWLEDGEMENT:
            </div>
            <div className="mt-2">
                <div className="flex flex-row col-12 mb-2 lg:col-12 lg:mb-0">
                    <Checkbox onChange={e => setDeclaration(e.checked)} checked={declaration} style={{ textAlign: "center" }}></Checkbox>
                    <label style={{ marginBottom: "5px", fontSize: "13px", marginLeft: "8px" }} >I hereby declare that the information given in this rate contract is true and correct to the best of my knowledge and belief.</label>
                </div>
            </div>
            <div className='mt-3 flex justify-content-end'>
                <Button label={"Add Vendor RC Item"} onClick={() => handleAddVendorRCItem()} disabled={!declaration} permissions={[228]}/>
            </div>

            <Dialog header='Confirmation' className="pi-punch-dialog" visible={isConfirmModalVisible} style={{ width: '50vw', height: '50vh', backgroundColor: '#f8f8f8' }} onHide={() => setIsConfirmModalVisible(false)} >
                <ConfirmModal 
                    vendorRateContractItems={[vendorRateContractItemObj]}
                    vendorRateContract={vendorRateContract}
                    setIsConfirmModalVisible={setIsConfirmModalVisible}
                    materialDetails={materialDetails}
                    materialBrands={materialBrands}
                    setVendorRateContract={setVendorRateContract}
                    setVisibleAddVendorRCItemModal={setVisibleAddVendorRCItemModal}
                />
            </Dialog>
        </>
    )
}

export default AddVendorRCItem